/**
 * Scenario Analysis Calculation Engine
 * All pure functions — no side effects.
 */

function safeNum(n) {
    return Number.isFinite(n) ? n : 0;
}

/**
 * Monthly mortgage payment (PMT formula)
 */
export function pmtCalc(annualRate, years, principal) {
    if (principal <= 0) return 0;
    if (annualRate <= 0) return principal / (years * 12);
    const r = annualRate / 100 / 12;
    const n = years * 12;
    return principal * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
}

/**
 * Calculate principal paid in year 1 (month-by-month loop)
 */
export function principalYear1(annualRate, loanAmount, monthlyPayment) {
    if (loanAmount <= 0 || monthlyPayment <= 0) return 0;
    const r = annualRate / 100 / 12;
    let balance = loanAmount;
    let totalPrincipal = 0;

    for (let m = 0; m < 12; m++) {
        const interest = balance * r;
        const principal = Math.min(monthlyPayment - interest, balance);
        if (principal <= 0) break;
        totalPrincipal += principal;
        balance -= principal;
    }
    return totalPrincipal;
}

/**
 * Full amortization schedule (year-by-year)
 */
export function getAmortizationSchedule(annualRate, loanAmount, monthlyPayment, years) {
    const r = annualRate / 100 / 12;
    let balance = loanAmount;
    const schedule = [];

    for (let year = 1; year <= years; year++) {
        let yearPrincipal = 0;
        let yearInterest = 0;
        for (let m = 0; m < 12; m++) {
            if (balance <= 0) break;
            const interest = balance * r;
            const principal = Math.min(monthlyPayment - interest, balance);
            yearPrincipal += principal;
            yearInterest += interest;
            balance = Math.max(0, balance - principal);
        }
        schedule.push({
            year,
            principal: yearPrincipal,
            interest: yearInterest,
            balance,
        });
    }
    return schedule;
}

/**
 * Get deal rating from Cash-on-Cash ROI
 */
function getDealRating(cocROI) {
    if (cocROI >= 8) return { label: 'Excellent', color: '#059669' };
    if (cocROI >= 5) return { label: 'Good', color: '#10b981' };
    if (cocROI >= 0) return { label: 'Marginal', color: '#f59e0b' };
    return { label: 'Negative', color: '#ef4444' };
}

/**
 * Main calculation — takes all input parameters, returns all outputs.
 */
export function calculateScenario(inputs) {
    const {
        purchasePrice = 0,
        improvements = 0,
        closingCosts = 10000,
        downPaymentPercent = 20,
        interestRate = 6.89,
        loanTerm = 30,
        rentPerUnit = 0,
        numberOfUnits = 1,
        vacancyRate = 5,
        annualRentIncrease = 2,
        propertyTaxes = 2400,
        insurance = 800,
        maintenanceCapex = 2000,
        propertyManagementFee = 8,
        hoa = 0,
        sewer = 0,
        water = 0,
        lawnCare = 0,
        tenantPlacementFee = 0,
        leaseRenewalFee = 0,
        annualTuneupFee = 0,
        annualExpenseIncrease = 2,
        appreciationRate = 3,
        isDIY = false,
    } = inputs;

    // Loan
    const downPaymentAmount = safeNum(purchasePrice * (downPaymentPercent / 100));
    const loanAmount = safeNum(purchasePrice - downPaymentAmount);

    // Mortgage
    const monthlyMortgage = safeNum(pmtCalc(interestRate, loanTerm, loanAmount));

    // Income
    const grossRentalIncome = safeNum(rentPerUnit * numberOfUnits * 12);
    const effectiveGrossIncome = safeNum(grossRentalIncome * (1 - vacancyRate / 100));

    // Expenses
    const pmFee = isDIY ? 0 : safeNum(effectiveGrossIncome * (propertyManagementFee / 100));
    const annualOptional = safeNum((hoa + sewer + water) * numberOfUnits * 12);
    const lawnAnnual = safeNum(lawnCare * 7);
    const totalOperatingExpenses = safeNum(
        propertyTaxes + insurance + maintenanceCapex + pmFee +
        annualOptional + lawnAnnual + tenantPlacementFee +
        leaseRenewalFee + annualTuneupFee
    );

    // NOI & Cash Flow
    const netOperatingIncome = safeNum(effectiveGrossIncome - totalOperatingExpenses);
    const annualDebtService = safeNum(monthlyMortgage * 12);
    const annualCashFlow = safeNum(netOperatingIncome - annualDebtService);
    const monthlyCashFlow = safeNum(annualCashFlow / 12);

    // Returns
    const cashInvested = safeNum(downPaymentAmount + improvements + closingCosts);
    const cashOnCashROI = cashInvested > 0 ? safeNum((annualCashFlow / cashInvested) * 100) : 0;
    const capRate = purchasePrice > 0 ? safeNum((netOperatingIncome / purchasePrice) * 100) : 0;
    const pYear1 = safeNum(principalYear1(interestRate, loanAmount, monthlyMortgage));
    const totalROI = cashInvested > 0
        ? safeNum(((annualCashFlow + pYear1) / cashInvested) * 100)
        : 0;

    const rating = getDealRating(cashOnCashROI);

    // ---- Projections (10-year) ----
    const fixedExpenses = propertyTaxes + insurance + maintenanceCapex +
        annualOptional + lawnAnnual + tenantPlacementFee +
        leaseRenewalFee + annualTuneupFee;

    const cashFlowProjection = [];
    const equityData = [];
    const roiComparison = [];
    let cumCashFlow = 0;

    const amortization = getAmortizationSchedule(interestRate, loanAmount, monthlyMortgage, loanTerm);

    for (let y = 1; y <= 10; y++) {
        const rentMult = Math.pow(1 + annualRentIncrease / 100, y - 1);
        const expMult = Math.pow(1 + annualExpenseIncrease / 100, y - 1);
        const yearEGI = effectiveGrossIncome * rentMult;
        const yearPM = isDIY ? 0 : yearEGI * (propertyManagementFee / 100);
        const yearExpenses = fixedExpenses * expMult + yearPM;
        const yearNOI = yearEGI - yearExpenses;
        const yearCashFlow = yearNOI - annualDebtService;

        cumCashFlow += yearCashFlow;

        cashFlowProjection.push({
            year: y,
            cashFlow: safeNum(yearCashFlow),
        });

        // Equity
        const propertyValue = purchasePrice * Math.pow(1 + appreciationRate / 100, y);
        const remainingBalance = y <= amortization.length ? amortization[y - 1].balance : 0;
        const equity = propertyValue - remainingBalance;
        equityData.push({
            year: y,
            equity: safeNum(equity),
            propertyValue: safeNum(propertyValue),
        });

        // ROI comparison
        const cumPrincipal = loanAmount - remainingBalance;
        const appreciationGain = propertyValue - purchasePrice;
        const cocROI_cum = cashInvested > 0 ? (cumCashFlow / cashInvested) * 100 : 0;
        const totalROI_cum = cashInvested > 0
            ? ((cumCashFlow + cumPrincipal + appreciationGain) / cashInvested) * 100
            : 0;
        roiComparison.push({
            year: y,
            cocROI: safeNum(cocROI_cum),
            totalROI: safeNum(totalROI_cum),
        });
    }

    // Donut data (income breakdown)
    const donutData = [
        { name: 'Cash Flow', value: Math.max(0, annualCashFlow) },
        { name: 'Debt Service', value: annualDebtService },
        { name: 'Operating Expenses', value: totalOperatingExpenses },
    ];

    return {
        // Loan
        downPaymentAmount,
        loanAmount,
        // Income
        grossRentalIncome,
        effectiveGrossIncome,
        // Expenses
        totalOperatingExpenses,
        pmFee,
        // Core metrics
        netOperatingIncome,
        monthlyMortgage,
        annualDebtService,
        annualCashFlow,
        monthlyCashFlow,
        cashInvested,
        cashOnCashROI,
        capRate,
        principalPaidYear1: pYear1,
        totalROI,
        dealRating: rating.label,
        dealRatingColor: rating.color,
        // Projections
        equityData,
        cashFlowProjection,
        amortizationData: amortization,
        roiComparison,
        donutData,
    };
}

/**
 * Build default assumptions from property data
 */
export function getDefaultAssumptions(property) {
    const price = property?.list_price || 0;
    const rent = property?.estimated_rent || Math.round(price * 0.01);
    const rehab = property?.renovation_cost || 0;

    return {
        purchasePrice: price,
        improvements: rehab,
        closingCosts: 10000,
        downPaymentPercent: 20,
        interestRate: 6.89,
        loanTerm: 30,
        rentPerUnit: rent,
        numberOfUnits: 1,
        vacancyRate: 5,
        annualRentIncrease: 2,
        propertyTaxes: 2400,
        insurance: 800,
        maintenanceCapex: 2000,
        propertyManagementFee: 8,
        hoa: 0,
        sewer: 0,
        water: 0,
        lawnCare: 0,
        tenantPlacementFee: 0,
        leaseRenewalFee: 0,
        annualTuneupFee: 0,
        annualExpenseIncrease: 2,
        appreciationRate: 3,
        isDIY: false,
    };
}
